import React, { useState, useRef, useEffect } from 'react';
import { OutlineChevronDownIcon } from '@tempmee/react-icons';
import '../../themes/MultiSelect.css';
import useDebounce from '../../hooks/useDebounce';

/* The design system has no multi-select yet, so the DS v3 look is grafted onto
   this component instead: passing `label` switches the trigger to the filled
   TextField/Select chrome (label over value, chevron, mint fill, divider
   underline), built from the same compiled utility classes InputSelectField
   uses. The dropdown behaviour is unchanged. Delete the variant if the design
   system grows a real multi-select. */
const FIELD_TRIGGER_CLASSES =
  'multiselect--field relative flex w-full items-center gap-1 rounded-t-[4px] ' +
  'border-b border-solid border-divider bg-[#f6f9f8] px-3 py-2 text-left ' +
  'transition-colors focus-visible:border-primary focus-visible:outline-none';

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  className,
  style,
  optionsStyle,
  placeholder,
  alwaysShowPlaceholder = false,
  autoApply = true,
  showAllButton = false,
  debounceTime = 500,
  Icon,
  maxSelection,
  label,
  ariaLabel,
}) => {
  const [selectedOptions, setSelectedOptions] = useState(value || []);

  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const optionsRef = useRef(null);

  const handleToggleOptions = (event) => {
    if (Array.from(event.target.classList).includes('multiselect__apply')) {
      setIsOptionsVisible(false);
    } else {
      setIsOptionsVisible(true);
    }
  };

  const handleOutsideClick = (event) => {
    if (optionsRef.current && !optionsRef.current.contains(event.target)) {
      setIsOptionsVisible(false);
    }
  };

  const emitOnChange = (options) => {
    if (onChange) {
      onChange(options);
    }
  };

  const emitOnChangeDebounced = useDebounce(emitOnChange, debounceTime);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (autoApply) {
      emitOnChangeDebounced(selectedOptions);
    }
  }, [selectedOptions]);

  // inner state of `selectedOptions` breaking the `value` change, useEffect is needed
  // refactor might be needed to remove `selectedOptions` redundancy
  useEffect(() => {
    setSelectedOptions(value);
  }, [value]);

  // Plain-text summary for the field variant; the CSS truncates the overflow.
  const summaryText = () => {
    if (alwaysShowPlaceholder || value.length === 0) {
      return placeholder || 'Select';
    }

    if (value.length === options.length) {
      return 'All';
    }

    return value
      .map((option) => options?.find((opt) => opt.value === option)?.label || option)
      .join(', ');
  };

  const renderPlaceholder = () => {
    if (alwaysShowPlaceholder || value.length === 0) {
      return <span className="multiselect__placeholder">{placeholder || 'Select'}</span>;
    }

    if (value.length === options.length) {
      return <span className="multiselect__placeholder">All</span>;
    }

    return value.map((option) => {
      const selectedOption = options?.find((opt) => opt.value === option);

      return (
        <span key={option} className="multiselect__selected-option">
          {selectedOption?.label || option}
        </span>
      );
    });
  };

  return (
    <button
      type="button"
      // The field variant's own content is "<label><summary>" ("RAM" + "All"),
      // so it needs an explicit name; `label` doubles as it. Callers on the
      // legacy variant pass `ariaLabel` because their caption is a sibling <p>.
      aria-label={ariaLabel || label}
      onClick={handleToggleOptions}
      className={
        label ? `${FIELD_TRIGGER_CLASSES} ${className || ''}` : `multiselect ${className}`
      }
      style={style}
      title={value
        ?.map((option) => {
          const selectedOption = options?.find((opt) => opt.value === option);
          return selectedOption?.label || option;
        })
        .join(', ')}
    >
      {label ? (
        <>
          <span className="relative flex h-10 min-w-0 flex-1 items-center">
            <span className="pointer-events-none absolute left-0 top-0 origin-left text-[12px] font-normal leading-4 tracking-[0.15px] text-text-primary">
              {label}
            </span>
            <span className="absolute inset-0 block h-full w-full truncate pt-4 text-left text-base font-semibold leading-6 text-text-primary">
              {summaryText()}
            </span>
          </span>
          <OutlineChevronDownIcon
            aria-hidden
            className={[
              'ml-1 size-6 shrink-0 text-text-secondary transition-transform duration-150',
              isOptionsVisible ? 'rotate-180' : '',
            ].join(' ')}
          />
        </>
      ) : (
        <div className="multiselect__selected">
          {Icon && (
            <img
              src={Icon}
              style={{ objectFit: 'contain', marginRight: 5, marginBottom: 2 }}
              alt="custom_toggle_icon"
            />
          )}

          {renderPlaceholder()}
        </div>
      )}

      {isOptionsVisible && (
        <div ref={optionsRef} className="multiselect__options_wrapper" style={optionsStyle}>
          {showAllButton && (
            <div className="multiselect__top_buttons">
              <span
                className="multiselect__all"
                onClick={() => {
                  if (selectedOptions.length === options.length) {
                    setSelectedOptions([]);
                    return;
                  }

                  setSelectedOptions(options.map((option) => option.value));
                }}
              >
                <input
                  aria-label="Select all"
                  name="checkAll"
                  type="checkbox"
                  checked={selectedOptions.length === options.length}
                />
                All
              </span>
            </div>
          )}

          <ul className="multiselect__options">
            {options.map((option) => (
              <li
                key={option.value}
                className={`multiselect__option ${
                  selectedOptions.includes(option.value) ? 'multiselect__option--selected' : ''
                }`}
                onClick={() => {
                  setSelectedOptions((prevSelectedOptions) => {
                    const isSelected = prevSelectedOptions.includes(option.value);
                    let updatedOptions = [];

                    if (isSelected) {
                      // Remove the option from the list
                      updatedOptions = prevSelectedOptions.filter(
                        (prevOption) => prevOption !== option.value,
                      );
                    } else if (
                      (maxSelection && prevSelectedOptions.length < maxSelection) ||
                      !maxSelection
                    ) {
                      // Add the option to the list
                      updatedOptions = [...prevSelectedOptions, option.value];
                    } else if (maxSelection && prevSelectedOptions.length >= maxSelection) {
                      updatedOptions = [...prevSelectedOptions];
                    }

                    return updatedOptions;
                  });
                }}
              >
                <input
                  name={option.value}
                  type="checkbox"
                  checked={selectedOptions.includes(option.value)}
                />
                {option.label}
              </li>
            ))}
          </ul>

          {!autoApply && (
            <div className="multiselect__bottom_buttons">
              <button
                disabled={
                  selectedOptions.length === 0 ||
                  JSON.stringify(selectedOptions) === JSON.stringify(value)
                }
                className="btn btn-info multiselect__apply"
                type="button"
                onClick={() => {
                  emitOnChange(selectedOptions);
                  setIsOptionsVisible(false);
                }}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </button>
  );
};

export default MultiSelect;
